import axios from "axios"

// Make sure API_URL is pointing to the backend on port 3000
const API_URL = `${process.env.REACT_APP_API_URL || "http://localhost:3000"}/api`

// Create an axios instance with credentials
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important for cookies
  headers: {
    "Content-Type": "application/json",
  },
})

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // If the error is 401 (Unauthorized) and it's not a retry
    if (error.response?.status === 401 && error.response?.data?.expired && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        console.log("Token expired, attempting to refresh...")
        // Call the refresh token endpoint
        await axios.post(`${API_URL}/auth/refresh-token`, {}, { withCredentials: true })
        console.log("Token refreshed successfully")

        // Retry the original request
        return api(originalRequest)
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError)
        // If refresh token fails, redirect to login
        window.location.href = "/login"
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)

// Check if user is authenticated
export const checkAuth = async () => {
  console.log("Checking authentication status...")
  try {
    // Try to get user data with the current token
    const response = await api.get("/protected")
    console.log("Auth check successful:", response.data)
    return { isAuthenticated: true, user: response.data.user }
  } catch (error) {
    console.log("Initial auth check failed:", error.response?.status)

    // If token is expired, try to refresh
    if (error.response?.status === 401 && error.response?.data?.expired) {
      try {
        console.log("Attempting to refresh token during auth check...")
        // Call refresh token endpoint
        const refreshResponse = await api.post("/auth/refresh-token")
        console.log("Token refresh successful during auth check")

        // If refresh successful, return the user data
        return {
          isAuthenticated: true,
          user: refreshResponse.data.user,
        }
      } catch (refreshError) {
        console.error("Token refresh failed during auth check:", refreshError)
        return { isAuthenticated: false, user: null }
      }
    }

    // For any other error, user is not authenticated
    console.log("User is not authenticated")
    return { isAuthenticated: false, user: null }
  }
}

// Login function
export const loginUser = async (credentials) => {
  console.log("Attempting login with credentials:", { ...credentials, password: "[REDACTED]" })
  const response = await api.post("/auth/login", credentials)
  console.log("Login successful:", response.data)
  return response.data
}

// Logout function
export const logoutUser = async () => {
  try {
    console.log("Attempting logout...")
    await api.post("/auth/logout")
    console.log("Logout successful")
    return true
  } catch (error) {
    console.error("Logout error:", error)
    return false
  }
}

// Payment settings functions
export const getPaymentSettings = async () => {
  try {
    const response = await api.get("/payment-settings")
    return response.data
  } catch (error) {
    console.error("Error fetching payment settings:", error)
    throw error
  }
}

export const getAdminPaymentSettings = async () => {
  try {
    const response = await api.get("/admin/payment-settings")
    return response.data
  } catch (error) {
    console.error("Error fetching admin payment settings:", error)
    throw error
  }
}

export const updatePaymentSettings = async (settings) => {
  try {
    const response = await api.put("/admin/payment-settings", settings)
    return response.data
  } catch (error) {
    console.error("Error updating payment settings:", error)
    throw error
  }
}

// QR Code image upload function
export const uploadQRCodeImage = async (file) => {
  try {
    const formData = new FormData()
    formData.append("qrCodeImage", file)

    const response = await api.post("/admin/payment-settings/upload-qr", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  } catch (error) {
    console.error("Error uploading QR code image:", error)
    throw error
  }
}

// Remove QR Code image function
export const removeQRCodeImage = async () => {
  try {
    const response = await api.delete("/admin/payment-settings/qr-code")
    return response.data
  } catch (error) {
    console.error("Error removing QR code image:", error)
    throw error
  }
}

export const resetPaymentSettings = async () => {
  try {
    const response = await api.post("/admin/payment-settings/reset")
    return response.data
  } catch (error) {
    console.error("Error resetting payment settings:", error)
    throw error
  }
}

// New pricing settings functions
export const fetchPricingSettings = async () => {
  try {
    const response = await api.get("/pricing-settings") // Endpoint to fetch pricing plans
    return response.data
  } catch (error) {
    console.error("Error fetching pricing settings:", error)
    throw error
  }
}

export const updatePricingSettings = async (settings) => {
  try {
    const response = await api.put("/admin/pricing-settings", settings) // Endpoint to update pricing plans (admin only)
    return response.data
  } catch (error) {
    console.error("Error updating pricing settings:", error)
    throw error
  }
}